import * as React from 'react';
import {Suspense, useDeferredValue, useState, useTransition} from 'react';
import {graphql, useLazyLoadQuery, useMutation} from 'react-relay';
import {Link} from 'react-router-dom';
import {Search} from 'lucide-react';
import {OpsCustomersPageQuery} from './__generated__/OpsCustomersPageQuery.graphql';
import {OpsCustomersPageCreateMutation} from './__generated__/OpsCustomersPageCreateMutation.graphql';
import {inputClass, primaryButton} from './lib';

const QUERY = graphql`
    query OpsCustomersPageQuery($search: String) {
        customers(search: $search, first: 200) {
            nodes {
                id
                name
                email
                phone
                reservations { id status }
            }
        }
    }`;

const CREATE = graphql`
    mutation OpsCustomersPageCreateMutation($input: CreateCustomerInput!) {
        createCustomer(input: $input) {
            customer { id name }
            userErrors { message }
        }
    }`;

const CustomerTable: React.FC<{ search: string; fetchKey: number }> = ({search, fetchKey}) => {
    const data = useLazyLoadQuery<OpsCustomersPageQuery>(QUERY, {search: search || null}, {
        fetchKey, fetchPolicy: 'network-only',
    });
    const rows = data.customers.nodes;
    if (rows.length === 0) return <p className="text-gray-500 text-sm">No customers yet.</p>;

    return (
        <div className="border border-gray-800 divide-y divide-gray-900">
            {rows.map((c) => {
                const active = c.reservations.filter((r) => !['RETURNED', 'CANCELLED'].includes(r.status)).length;
                return (
                    <div key={c.id} className="flex flex-col md:flex-row md:items-center gap-1 md:gap-6 px-4 py-3">
                        <span className="text-white font-semibold md:w-56">{c.name}</span>
                        <span className="text-gray-400 text-sm md:w-64 truncate">{c.email ?? '—'}</span>
                        <span className="text-gray-400 text-sm md:w-40">{c.phone ?? '—'}</span>
                        <span className="text-gray-500 text-xs flex-1">
                            {c.reservations.length} reservation{c.reservations.length === 1 ? '' : 's'}
                            {active > 0 && <span className="text-lumina-accent"> · {active} active</span>}
                        </span>
                        <Link to={`/ops/reservations/new?customer=${encodeURIComponent(c.id)}`}
                              className="text-xs text-lumina-purple hover:text-white">Book →</Link>
                    </div>
                );
            })}
        </div>
    );
};

export const NewCustomerForm: React.FC<{ onCreated: (customer: { id: string; name: string }) => void }> = ({onCreated}) => {
    const [commit, inFlight] = useMutation<OpsCustomersPageCreateMutation>(CREATE);
    const [form, setForm] = useState({name: '', email: '', phone: ''});
    const [errors, setErrors] = useState<string[]>([]);

    const submit = (e: React.FormEvent) => {
        e.preventDefault();
        commit({
            variables: {input: {name: form.name, email: form.email || null, phone: form.phone || null}},
            onCompleted: (res) => {
                const payload = res.createCustomer;
                if (payload?.customer) {
                    setForm({name: '', email: '', phone: ''});
                    setErrors([]);
                    onCreated(payload.customer);
                } else {
                    setErrors(payload?.userErrors.map((u) => u.message) ?? ['Something went wrong']);
                }
            },
            onError: (err) => setErrors([err.message]),
        });
    };

    return (
        <form onSubmit={submit} className="flex flex-col md:flex-row gap-2">
            <input className={`${inputClass} flex-1`} placeholder="Name" required value={form.name}
                   onChange={(e) => setForm({...form, name: e.target.value})}/>
            <input className={`${inputClass} flex-1`} placeholder="Email" type="email" value={form.email}
                   onChange={(e) => setForm({...form, email: e.target.value})}/>
            <input className={`${inputClass} w-40`} placeholder="Phone" value={form.phone}
                   onChange={(e) => setForm({...form, phone: e.target.value})}/>
            <button type="submit" disabled={inFlight} className={primaryButton}>Add customer</button>
            {errors.length > 0 && <p className="text-red-400 text-xs self-center">{errors.join(', ')}</p>}
        </form>
    );
};

const OpsCustomersPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [fetchKey, setFetchKey] = useState(0);
    const [, startTransition] = useTransition();
    const deferredSearch = useDeferredValue(search);
    const refresh = () => startTransition(() => setFetchKey((k) => k + 1));

    return (
        <div>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
                <h1 className="text-3xl font-display font-bold">Customers</h1>
                <label className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"/>
                    <input value={search} onChange={(e) => setSearch(e.target.value)}
                           placeholder="Search name, email, phone…" className={`${inputClass} pl-9 w-80`}/>
                </label>
            </div>
            <div className="mb-6 p-4 border border-gray-800 bg-black/40">
                <NewCustomerForm onCreated={refresh}/>
            </div>
            <Suspense fallback={<p className="text-gray-500 text-sm">Loading…</p>}>
                <CustomerTable search={deferredSearch} fetchKey={fetchKey}/>
            </Suspense>
        </div>
    );
};

export default OpsCustomersPage;
